import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FamilyProvider } from './context/FamilyContext';
import { UIProvider, useUI } from './context/UIContext';
import { I18nProvider } from './i18n';

import LoadingScreen from './components/LoadingScreen';
import ReauthOverlay from './components/ReauthOverlay';
import SessionExpiredModal from './components/SessionExpiredModal';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import OnboardingModal from './components/OnboardingModal';
import LanguageSwitcher from './components/LanguageSwitcher';
import AccountSettingsModal from './components/AccountSettingsModal';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MyFamily from './pages/MyFamily';
import MyCamps from './pages/MyCamps';
import MyCoaching from './pages/MyCoaching';
import Documents from './pages/Documents';
import CourseDetail from './pages/CourseDetail';

import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 0, refetchOnWindowFocus: true, refetchOnMount: true },
  },
});

function AppContent() {
  const { isLoading, isAuthenticated, needOnboarding, isReauthing, isSessionExpired } = useAuth();
  const { showAccountSettings, setShowAccountSettings } = useUI();

  if (isReauthing) return <ReauthOverlay />;
  if (isLoading) return <LoadingScreen message="Checking your authentication status..." />;

  if (isAuthenticated && needOnboarding) return <OnboardingModal />;

  return (
    <>
      {isAuthenticated && <Header />}
      <Routes>
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/my-family" element={<ProtectedRoute><MyFamily /></ProtectedRoute>} />
        <Route path="/my-camps" element={<ProtectedRoute><MyCamps /></ProtectedRoute>} />
        <Route path="/my-coaching" element={<ProtectedRoute><MyCoaching /></ProtectedRoute>} />
        <Route path="/my-coaching/:courseId" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
        <Route path="/documents/:campId" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
        <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
        <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
      </Routes>
      {isAuthenticated && <LanguageSwitcher />}
      {showAccountSettings && <AccountSettingsModal onClose={() => setShowAccountSettings(false)} />}
      {isSessionExpired && <SessionExpiredModal />}
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <AuthProvider>
          <FamilyProvider>
            <UIProvider>
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </UIProvider>
          </FamilyProvider>
        </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
